import { Overlay } from '@eti/components';
import { type ReactNode, type MouseEvent } from 'react';
import type { TripType } from '@eti/schema-types';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { Stack } from '@etg/wings';
import TripItinerary from './TripItinerary';
import type { Bound } from './models';

const headerStyles = css`
  position: relative;

  & > button {
    inset-inline-start: 8px;
    position: absolute;
  }
`;

export const getJourneyType = (tripType: TripType) => {
  switch (tripType) {
    case 'ONE_WAY':
      return 'Result.TripDetails.OneWay.Title';
    case 'RETURN':
      return 'Result.TripDetails.ReturnTrip.Title';
    default:
      return 'Result.TripDetails.MultiStopTrip.Title';
  }
};

interface TripItineraryDrawerProps {
  afterItineraryContent?: ReactNode;
  beforeItineraryContent?: ReactNode;
  bounds: Bound[];
  footer?: ReactNode;
  id?: string;
  isOpen: boolean;
  onClose: (event: MouseEvent<HTMLButtonElement>) => void;
  shouldHaveHeaderWithBackButton?: boolean;
  shouldShowTripItinerary?: boolean;
  title: string;
  tripType: TripType;
}

const TripItineraryDrawer = ({
  afterItineraryContent,
  beforeItineraryContent,
  bounds,
  footer,
  id,
  isOpen,
  onClose,
  shouldHaveHeaderWithBackButton,
  shouldShowTripItinerary = true,
  title,
  tripType,
}: TripItineraryDrawerProps) => {
  const { t } = useTranslation();

  return (
    <Overlay.Drawer dataTestId="itinerary-drawer" handleClose={onClose} id={id} isOpen={isOpen}>
      {shouldHaveHeaderWithBackButton ? (
        <Overlay.HeaderWithBackButton
          className={headerStyles}
          journeyType={t(getJourneyType(tripType))}
          onClose={onClose}
          title={title}
        />
      ) : (
        <Overlay.Header onClose={onClose} title={title} />
      )}

      <Overlay.Content data-testid="itinerary-drawer-content">
        <Stack>
          {beforeItineraryContent}
          {shouldShowTripItinerary && <TripItinerary bounds={bounds} tripType={tripType} />}
          {afterItineraryContent}
        </Stack>
      </Overlay.Content>
      {footer && <Overlay.Footer data-testid="itinerary-drawer-footer">{footer}</Overlay.Footer>}
    </Overlay.Drawer>
  );
};

export default TripItineraryDrawer;
